import { z } from 'zod';

export const AUCTION_EVENTS = {
  LiveBiddingStarted: 'live_bidding_started',
  LotSold: 'lot_sold',
  NewBid: 'new_bid',
  OpenBidding: 'open_bidding',
  CloseBidding: 'close_bidding',
  LotUnsold: 'lot_unsold',
  SkipToLot: 'skip_to_lot',
  PauseAuction: 'pause_auction',
  ResumeAuction: 'resume_auction',
  EndAuction: 'end_auction',
  ResetWarning: 'reset_warning',
  FairWarning: 'fair_warning',
  FinalWarning: 'final_warning',
  NewMessage: 'new_message',
  LockBid: 'lock_bid',
  UnlockBid: 'unlock_bid',
  SelectProduct: 'select_product',
} as const;

export const AuctionEventEnum = z.nativeEnum(AUCTION_EVENTS);

export const AuctionEventSchemas = {
  [AUCTION_EVENTS.LiveBiddingStarted]: z.object({
    productId: z.string(),
  }),
  [AUCTION_EVENTS.LotSold]: z.object({
    productId: z.string(),
    auctionId: z.string().optional(),
    bidId: z.string().optional(),
  }),
  [AUCTION_EVENTS.NewBid]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.OpenBidding]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.CloseBidding]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.LotUnsold]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.SkipToLot]: z.object({
    auctionId: z.string(),
    productId: z.string(),
    nextProductId: z.string().optional(),
  }),
  [AUCTION_EVENTS.PauseAuction]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.ResumeAuction]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.EndAuction]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.ResetWarning]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
    metadata: z
      .object({
        fairWarningCount: z.number().nullish(),
      })
      .optional(),
  }),
  [AUCTION_EVENTS.FairWarning]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
    metadata: z
      .object({
        fairWarningCount: z.number().nullish(),
      })
      .optional(),
  }),
  [AUCTION_EVENTS.FinalWarning]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.NewMessage]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.LockBid]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.UnlockBid]: z.object({
    auctionId: z.string(),
    productId: z.string().optional(),
  }),
  [AUCTION_EVENTS.SelectProduct]: z.object({
    auctionId: z.string(),
    productId: z.string(),
  }),
} as const;
export type AuctionEventPayloads = {
  [K in keyof typeof AUCTION_EVENTS]: z.infer<(typeof AuctionEventSchemas)[(typeof AUCTION_EVENTS)[K]]>;
};

type ValidatedAuctionEvent = {
  [K in keyof typeof AUCTION_EVENTS]: {
    event: (typeof AUCTION_EVENTS)[K];
    data: AuctionEventPayloads[K];
  };
}[keyof typeof AUCTION_EVENTS];
export function validateAuctionEvent(payload: { event: string; data: unknown }): ValidatedAuctionEvent {
  const parsedPayload = z
    .object({
      event: AuctionEventEnum,
      data: z.unknown(),
    })
    .parse(payload);

  const schema = AuctionEventSchemas[parsedPayload.event];
  const parsedData = schema.parse(parsedPayload.data);

  return {
    event: parsedPayload.event,
    data: parsedData,
  } as ValidatedAuctionEvent;
}
